/**
 * A saved-item row as `GET /saved-items` returns it — the raw savedsearch-api record, no lot data.
 * `lotId` is hydrated into a card on the front end (item-summaries); the bookkeeping fields
 * (`lastProcessed`/`lastSent`/`lastStatusMessage`) come straight from the email-fanout worker.
 */
export type SimilarItemAlert = {
    lastProcessed: string;
    lastSent: string;
    lastStatusMessage: string;
    lotId: number;
    lotTitle: string;
    marketplace: string;
    savedAt: string;
    savedItemId: number;
    sendAlert: boolean;
    userId: number;
};

/** `GET /saved-items` sort values (savedsearch-api SEAR-1700). */
export type SimilarItemAlertsSort = 'savedNewest' | 'savedOldest' | 'titleAZ' | 'titleZA';

export type SimilarItemAlertsListStatus = 'idle' | 'loading' | 'success' | 'error';

/** The paginated management list backing the Emails & Notifications section (and the For You tab). */
export type SimilarItemAlertsList = {
    items: SimilarItemAlert[];
    page: number;
    pageSize: number;
    status: SimilarItemAlertsListStatus;
    totalRecords: number;
};

export type SimilarItemAlertsSlice = {
    /**
     * Keyed by lotId. The value is the alert's savedItemId, or 0 when the lot has no alert — the
     * same sentinel savedsearch-api returns from `GET /saved-items/check`.
     */
    byId: { [lotId: number]: number };
    /** The management list (Emails & Notifications section / For You tab). */
    list: SimilarItemAlertsList;
    /** Keyed by lotId. The epoch ms the lot's alert state was last resolved, for cache staleness. */
    loaded: { [lotId: number]: number };
    /** lotIds with a lookup in flight. */
    loading: number[];
    /**
     * lotIds with a set/remove in flight. Kept apart from `loading` because the two run at
     * different cadences and a shared flag makes them block each other — post-login, the mount
     * lookup and the user's queued set fire together, and a shared flag let the lookup swallow
     * the set.
     */
    saving: number[];
};

export const DEFAULT_SIMILAR_ITEM_ALERTS_PAGE_SIZE = 24;

export const defaultSimilarItemAlertsList: SimilarItemAlertsList = {
    items: [],
    page: 1,
    pageSize: DEFAULT_SIMILAR_ITEM_ALERTS_PAGE_SIZE,
    status: 'idle',
    totalRecords: 0,
};

export const defaultSimilarItemAlertsSlice: SimilarItemAlertsSlice = {
    byId: {},
    list: defaultSimilarItemAlertsList,
    loaded: {},
    loading: [],
    saving: [],
};

export const NO_SIMILAR_ITEM_ALERT = 0;
